import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send, LoaderCircle, MessageSquareQuote } from "lucide-react";
import PageHero from "../components/PageHero";
import CtaBanner from "../components/CtaBanner";
import { Reveal } from "../components/Reveal";
import { Stars } from "../components/Stars";
import { INTERIOR_IMAGES } from "../data/site";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand";

export default function Recensioni() {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState({ nome: "", stelle: 5, commento: "" });
  const [hoverStar, setHoverStar] = useState(0);
  const [sending, setSending] = useState(false);
  const { t, lang } = useLang();

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch {
      toast.error(t.reviews.loadErr);
    }
  }, [t.reviews.loadErr]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success(t.reviews.toastOk);
      setForm({ nome: "", stelle: 5, commento: "" });
      fetchReviews();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(t.reviews.toastErr, {
        description: typeof detail === "string" ? detail : t.reviews.toastErrDesc,
      });
    } finally {
      setSending(false);
    }
  };

  const avg = reviews && reviews.length ? reviews.reduce((s, r) => s + r.stelle, 0) / reviews.length : 0;
  const locale = lang === "it" ? "it-IT" : "en-GB";

  return (
    <div data-testid="recensioni-page">
      <PageHero
        overline={t.reviews.overline}
        title={t.reviews.title}
        desc={t.reviews.desc}
        images={[INTERIOR_IMAGES.kitchen, INTERIOR_IMAGES.living]}
        imageAlt={t.reviews.imageAlt}
      />

      <section className="bg-white py-20 sm:py-24" data-testid="reviews-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <div className="rounded-2xl border border-line bg-white p-7 shadow-sm sm:p-8">
                <div className="flex items-center gap-4 border-b border-line pb-6" data-testid="reviews-summary">
                  <span className="font-heading text-5xl font-extrabold text-slate-900" data-testid="reviews-average">
                    {avg.toFixed(1)}
                  </span>
                  <div>
                    <Stars value={Math.round(avg)} size={20} />
                    <p className="mt-1.5 text-xs text-slate-500" data-testid="reviews-count">
                      {reviews ? `${reviews.length} ${reviews.length === 1 ? t.reviews.one : t.reviews.many}` : t.reviews.loading}
                    </p>
                  </div>
                </div>

                <h2 className="mt-6 font-heading text-lg font-semibold text-slate-900">{t.reviews.formTitle}</h2>
                <form onSubmit={submit} className="mt-5 space-y-5" data-testid="review-form">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="rev-nome">{t.reviews.name}</label>
                    <input id="rev-nome" required minLength={2} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder={t.reviews.namePh} className={inputCls} data-testid="review-name-input" />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t.reviews.rating}</label>
                    <div className="flex gap-1.5" data-testid="review-stars-input">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setForm({ ...form, stelle: n })}
                          onMouseEnter={() => setHoverStar(n)}
                          onMouseLeave={() => setHoverStar(0)}
                          className="rounded-md p-1 transition-transform duration-150 hover:scale-110"
                          aria-label={`${n}`}
                          data-testid={`review-star-${n}`}
                        >
                          <Star size={28} className={n <= (hoverStar || form.stelle) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="rev-commento">{t.reviews.comment}</label>
                    <textarea id="rev-commento" required minLength={5} rows={4} value={form.commento} onChange={(e) => setForm({ ...form, commento: e.target.value })} placeholder={t.reviews.commentPh} className={`${inputCls} resize-none`} data-testid="review-comment-input" />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="review-submit-button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-amber disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? <LoaderCircle size={17} className="animate-spin" /> : <Send size={16} />}
                    {sending ? t.reviews.sending : t.reviews.submit}
                  </button>
                </form>
              </div>
            </Reveal>

            <div className="lg:col-span-3">
              {reviews && reviews.length === 0 && (
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 py-20 text-slate-400" data-testid="reviews-empty-state">
                  <MessageSquareQuote size={32} />
                  <p className="text-sm">{t.reviews.empty}</p>
                </div>
              )}
              <div className="space-y-4" data-testid="reviews-list">
                {reviews?.map((r, i) => (
                  <Reveal key={r.id} delay={Math.min(i * 0.06, 0.3)}>
                    <article className="rounded-2xl border border-line bg-white p-6 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5" data-testid={`review-card-${r.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3.5">
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand/10 font-heading text-base font-bold text-brand">
                            {r.nome.charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <p className="font-heading text-sm font-semibold text-slate-900">{r.nome}</p>
                            <Stars value={r.stelle} />
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(r.created_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-slate-600">{r.commento}</p>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
